import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
} from 'chart.js';
import { useEffect, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { ResponsiveContainer } from 'recharts'; // still usable as a wrapper

import { chartTimePeriod } from '../../../config/enum';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import type { RootState } from '../../../redux/store';
import BatteryLoader from '../../../shared-components/battery-loader/BatteryLoader';
import CustomToggleButton from '../../../shared-components/custom-toggle-button/CustomToggleButton';
import {
  fillMissingLastNDaysGeneric,
  fillMissingMonthsGeneric,
} from '../../../shared-functions/ChartFunctions';
import stationManagement from '../../../utils/api/station-management';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

interface ChartDataProperties {
  label: string; // month/day/hour
  totalRevenue: number;
  totalAmountPaid: number;
  totalPayableAmount: number;
  monthName: string;
}
/**
 * Function to fill missing 24 hours generic data
 * @param {any[]} data The input data array.
 * @returns {any[]} The data with missing hours filled.
 */
const fillMissingLast24HrsGeneric = (data: any[]) => {
  const result: any[] = [];
  const now = new Date();

  for (let index = 23; index >= 0; index -= 1) {
    const date = new Date(now);
    date.setHours(now.getHours() - index);

    const hour = date.getHours();
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    const found = data.find(
      (d) => d.hour === hour && d.day === day && d.month === month && d.year === year,
    );

    result.push({
      label: String(hour),
      year,
      month,
      day,
      stationDetails: found?.stationDetails ?? [],
    });
  }

  return result;
};

/**
 * Uptime TrendOver Time Chart
 * @returns {any} The Uptime Trend Over Time Chart component.
 */
function UptimeTrendOverTimeChart() {
  const { t } = useTranslation('stationManagement');
  const { selectedStationIds } = useAppSelector((state) => state.stationComparison);
  const dispatch = useAppDispatch();
  const [graphData, setGraphData] = useState<ChartDataProperties[]>([]);
  const [chartData, setChartData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  //    const [graphData, setGraphData] = useState<(Partial<ChartDataProperties> & { label: string })[]>(
  //     [],
  //   );
  const chargingStationList = useAppSelector(
    (state: RootState) => state.stationManagement.chargingStationList,
  );
  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          /**
           * hides all legend items (no “radio buttons”)
           * @returns \
           */
          filter: () => false,
        },
      },
      tooltip: {
        backgroundColor: '#1F2937',
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        padding: 10,
        cornerRadius: 8,
        displayColors: true,
      },
    },
    interaction: {
      intersect: false,
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
      },
      y: {
        grid: { display: false }, //  remove Y grid lines
        border: { display: false }, //  remove Y axis line
        // ticks: { display: false }, //  remove Y axis numbers
        ticks: {
          display: true, // SHOW ticks
          count: 5,
          /**
           * Showing value with kW
           * @param {number | string} value The data value.
           * @returns {string} The formatted value with unit.
           */
          callback: (value) => `${value} ${t('hrs')}`, // FORMAT ticks
          color: '#6b7280', // optional: nice gray
          font: {
            size: 11,
            weight: 500,
          },
        },
      },
    },
  };

  const stationIdsToUse = useMemo(() => {
    const selected = selectedStationIds.filter(Boolean);

    // If user selected stations → use them
    if (selected.length > 0) {
      return selected;
    }

    // Otherwise → use all charging stations
    return chargingStationList.map((station) => station._id);
  }, [selectedStationIds, chargingStationList]);

  /**
   * Fetches the revenue vs payable report data for the specified time range and updates the chart.
   * @param range - The time period for the report ('last24hours', 'last7days', 'lastMonth', or 'last12months').
   */
  const fetchReport = async (range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months') => {
    if (chargingStationList.length === 0) return;

    setLoading(true);
    try {
      const response = await stationManagement.fetchUptimeOverTimeChartData({
        timePeriod: range,
        chargingStations: stationIdsToUse,
      });
      if (response.status === 200) {
        const { data } = response;

        let filledData: any[] = [];
        switch (range) {
          case 'last12months':
            filledData = fillMissingMonthsGeneric(data);
            break;
          case 'lastMonth':
            filledData = fillMissingLastNDaysGeneric(data, 30);
            break;
          case 'last7days':
            filledData = fillMissingLastNDaysGeneric(data, 7);
            break;
          case 'last24hours':
            filledData = fillMissingLast24HrsGeneric(data);
            break;
          default:
            filledData = data;
        }

        setGraphData(filledData);
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      dispatch(
        setNotificationDetails({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || t('somethingWentWrong'),
          },
        }),
      );
    }
  };

  useEffect(() => {
    if (chargingStationList.length === 0) return;
    fetchReport(chartData || 'last24hours');
  }, [stationIdsToUse]);

  const labels = graphData.map((d) => d.label || d.monthName);

  // Extract all unique station IDs dynamically
  // Extract all unique station names
  const allStations = Array.from(
    new Set(graphData.flatMap((d: any) => (d.stationDetails || []).map((s: any) => s.stationName))),
  );

  const colorPalette = ['#00b894', '#6c5ce7', '#d63031', '#0984e3'];

  const datasets = allStations.map((stationName, index) => ({
    label: stationName,
    data: graphData.map((d: any) => {
      const station = d.stationDetails?.find((s: any) => s.stationName === stationName);
      return station?.totalHours ?? 0;
    }),
    borderColor: colorPalette[index % colorPalette.length],
    backgroundColor: `${colorPalette[index % colorPalette.length]}33`,
    fill: false,
    tension: 0.4,
    borderWidth: 1.5,
    pointRadius: 0,
    pointHoverRadius: 3,
  }));

  return (
    <div className="uptime-trend-over-time-chart">
      <div className="uptime-trend-over-time-container">
        <div className="uptime-trend-over-time-header">
          <div className="uptime-trend-over-time-title">{t('uptimeTrendOverTime')}</div>
          <CustomToggleButton
            labels={chartTimePeriod.map((item) => t(item.label))}
            onToggle={(index) => {
              const selected = chartTimePeriod[index];
              if (selected) {
                fetchReport(selected.value);
                setChartData(selected.value);
              }
            }}
            defaultActiveIndex={0}
            type="chart"
          />
        </div>
        <div className="uptime-trend-over-time-container">
          <div className="uptime-trend-over-time-chart">
            <ResponsiveContainer height={230}>
              {loading ? (
                <div className="loader">
                  <BatteryLoader />
                </div>
              ) : (
                <Line
                  data={{
                    labels,
                    datasets,
                  }}
                  options={chartOptions}
                />
              )}
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UptimeTrendOverTimeChart;
